import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { toDate } from '../shared/format'
import { NOTIFICATION_CATEGORIES, type AppNotification, type NotificationCategory, type NotificationLevel } from './types'

export const notificationKeys = { all: ['notifications'] as const }

const LEVELS = new Set<NotificationLevel>(['info', 'success', 'warning', 'critical'])

type Raw = {
  id: string
  category?: string
  level?: string
  title?: string
  body?: string
  link?: string | null
  actor?: string
  created_at?: string
  createdAt?: string
  read_at?: string | null
  readAt?: string | null
}

/** Sunucu tarihi "gg.aa.yyyy SS:dd:ss" (İstanbul) ya da ISO gönderebilir; sayfa sıralama/gruplama için ISO bekler. */
function isoOf(value: string | null | undefined): string | null {
  const d = toDate(value ?? null)
  return d ? d.toISOString() : null
}

export function mapNotification(raw: Raw): AppNotification {
  const category = (NOTIFICATION_CATEGORIES as readonly string[]).includes(raw.category ?? '') ? (raw.category as NotificationCategory) : 'system'
  const level = LEVELS.has(raw.level as NotificationLevel) ? (raw.level as NotificationLevel) : 'info'
  return {
    id: raw.id,
    category,
    level,
    title: raw.title ?? '',
    body: raw.body ?? '',
    link: raw.link ?? null,
    actor: raw.actor ?? '',
    createdAt: isoOf(raw.created_at ?? raw.createdAt) ?? new Date().toISOString(),
    readAt: isoOf(raw.read_at ?? raw.readAt),
  }
}

async function listNotifications() {
  const data = await api.get<Raw[] | { items?: Raw[] }>('/api/v1/notifications')
  return asList(data).map(mapNotification).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

function useMut<A>(fn: (arg: A) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }) })
}

export const useNotificationList = () => useQuery({ queryKey: notificationKeys.all, queryFn: listNotifications, refetchInterval: 60_000 })
export const useSetRead = () => useMut(({ id, read }: { id: string; read: boolean }) => api.post(`/api/v1/notifications/${id}/${read ? 'read' : 'unread'}`))
export const useMarkAllRead = () => useMut(() => api.post('/api/v1/notifications/read-all'))
export const useRemoveNotification = () => useMut((id: string) => api.del(`/api/v1/notifications/${id}`))
export const useRemoveRead = () => useMut(() => api.del('/api/v1/notifications/read'))
